// SPDX-License-Identifier: AGPL-3.0-or-later

export interface SoundboardSound {
	id: string;
	name: string;
	emoji: string;
	category: 'built-in' | 'server' | 'personal' | 'custom';
	guildId?: string;
	customUrl?: string;
}

export const DEFAULT_SOUNDBOARD_SOUNDS: ReadonlyArray<SoundboardSound> = [
	{id: 'airhorn', name: 'Airhorn', emoji: '🎺', category: 'built-in'},
	{id: 'badumtss', name: 'Ba-Dum-Tss', emoji: '🥁', category: 'built-in'},
	{id: 'cricket', name: 'Crickets', emoji: '🦗', category: 'built-in'},
	{id: 'levelup', name: 'Level Up', emoji: '🎮', category: 'built-in'},
	{id: 'tada', name: 'Tada Fanfare', emoji: '🎉', category: 'built-in'},
	{id: 'quack', name: 'Quack', emoji: '🦆', category: 'built-in'},
	{id: 'bruh', name: 'Bruh', emoji: '🔊', category: 'built-in'},
	{id: 'chime', name: 'Bell Chime', emoji: '🔔', category: 'built-in'},
];

/**
 * Procedurally synthesize built-in sound effects using standard Web Audio API.
 * This guarantees instant playback with zero external file downloads or latency!
 */
export function synthesizeSoundEffect(ctx: AudioContext, soundId: string, destination: AudioNode): number {
	const now = ctx.currentTime;

	switch (soundId) {
		case 'airhorn': {
			// Classic horn blast
			const freqs = [311.13, 370.0, 466.16]; // Eb4, F#4, Bb4 triad
			freqs.forEach((freq) => {
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'sawtooth';
				osc.frequency.setValueAtTime(freq, now);
				osc.frequency.exponentialRampToValueAtTime(freq * 0.95, now + 0.45);

				gain.gain.setValueAtTime(0.2, now);
				gain.gain.setValueAtTime(0.25, now + 0.35);
				gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

				osc.connect(gain);
				gain.connect(destination);
				osc.start(now);
				osc.stop(now + 0.6);
			});
			return 650;
		}

		case 'badumtss': {
			// Ba-dum (low kicks) then tss (white noise cymbal)
			// Ba
			const osc1 = ctx.createOscillator();
			const gain1 = ctx.createGain();
			osc1.frequency.setValueAtTime(140, now);
			osc1.frequency.exponentialRampToValueAtTime(50, now + 0.12);
			gain1.gain.setValueAtTime(0.4, now);
			gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
			osc1.connect(gain1);
			gain1.connect(destination);
			osc1.start(now);
			osc1.stop(now + 0.12);

			// Dum
			const osc2 = ctx.createOscillator();
			const gain2 = ctx.createGain();
			osc2.frequency.setValueAtTime(130, now + 0.18);
			osc2.frequency.exponentialRampToValueAtTime(45, now + 0.3);
			gain2.gain.setValueAtTime(0.45, now + 0.18);
			gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
			osc2.connect(gain2);
			gain2.connect(destination);
			osc2.start(now + 0.18);
			osc2.stop(now + 0.3);

			// Tss (cymbal noise buffer)
			const bufferSize = Math.floor(ctx.sampleRate * 0.4);
			const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
			const output = noiseBuffer.getChannelData(0);
			for (let i = 0; i < bufferSize; i++) {
				output[i] = Math.random() * 2 - 1;
			}
			const noiseSource = ctx.createBufferSource();
			noiseSource.buffer = noiseBuffer;

			const filter = ctx.createBiquadFilter();
			filter.type = 'highpass';
			filter.frequency.setValueAtTime(5000, now + 0.35);

			const noiseGain = ctx.createGain();
			noiseGain.gain.setValueAtTime(0.3, now + 0.35);
			noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

			noiseSource.connect(filter);
			filter.connect(noiseGain);
			noiseGain.connect(destination);
			noiseSource.start(now + 0.35);
			noiseSource.stop(now + 0.7);

			return 750;
		}

		case 'cricket': {
			// Periodic chirps
			for (let chirp = 0; chirp < 3; chirp++) {
				const startTime = now + chirp * 0.18;
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'sine';
				osc.frequency.setValueAtTime(4500, startTime);
				osc.frequency.linearRampToValueAtTime(4800, startTime + 0.08);

				gain.gain.setValueAtTime(0.18, startTime);
				gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.09);

				osc.connect(gain);
				gain.connect(destination);
				osc.start(startTime);
				osc.stop(startTime + 0.1);
			}
			return 600;
		}

		case 'levelup': {
			// Ascending 8-bit game arpeggio: C4, E4, G4, C5, E5, G5, C6
			const notes = [261.63, 329.63, 392.0, 523.25, 659.25, 783.99, 1046.5];
			notes.forEach((freq, idx) => {
				const noteTime = now + idx * 0.07;
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'square';
				osc.frequency.setValueAtTime(freq, noteTime);

				gain.gain.setValueAtTime(0.15, noteTime);
				gain.gain.exponentialRampToValueAtTime(0.001, noteTime + 0.15);

				osc.connect(gain);
				gain.connect(destination);
				osc.start(noteTime);
				osc.stop(noteTime + 0.16);
			});
			return 700;
		}

		case 'tada': {
			// Fanfare chord: C5 -> G5
			const osc1 = ctx.createOscillator();
			const osc2 = ctx.createOscillator();
			const gain = ctx.createGain();

			osc1.type = 'triangle';
			osc2.type = 'sine';
			osc1.frequency.setValueAtTime(523.25, now);
			osc1.frequency.setValueAtTime(783.99, now + 0.15);
			osc2.frequency.setValueAtTime(659.25, now);
			osc2.frequency.setValueAtTime(1046.5, now + 0.15);

			gain.gain.setValueAtTime(0.2, now);
			gain.gain.setValueAtTime(0.35, now + 0.15);
			gain.gain.exponentialRampToValueAtTime(0.001, now + 0.85);

			osc1.connect(gain);
			osc2.connect(gain);
			gain.connect(destination);

			osc1.start(now);
			osc2.start(now);
			osc1.stop(now + 0.85);
			osc2.stop(now + 0.85);
			return 900;
		}

		case 'quack': {
			// Pitch drop with formant filter
			const osc = ctx.createOscillator();
			const gain = ctx.createGain();
			const filter = ctx.createBiquadFilter();

			osc.type = 'sawtooth';
			osc.frequency.setValueAtTime(400, now);
			osc.frequency.exponentialRampToValueAtTime(200, now + 0.35);

			filter.type = 'bandpass';
			filter.frequency.setValueAtTime(1200, now);
			filter.Q.setValueAtTime(3.0, now);

			gain.gain.setValueAtTime(0.35, now);
			gain.gain.exponentialRampToValueAtTime(0.001, now + 0.38);

			osc.connect(filter);
			filter.connect(gain);
			gain.connect(destination);

			osc.start(now);
			osc.stop(now + 0.38);
			return 450;
		}

		case 'bruh': {
			// Resonant low bass impact
			const osc = ctx.createOscillator();
			const gain = ctx.createGain();

			osc.type = 'triangle';
			osc.frequency.setValueAtTime(120, now);
			osc.frequency.exponentialRampToValueAtTime(45, now + 0.45);

			gain.gain.setValueAtTime(0.5, now);
			gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);

			osc.connect(gain);
			gain.connect(destination);

			osc.start(now);
			osc.stop(now + 0.5);
			return 550;
		}

		case 'chime':
		default: {
			// Glass chime
			const freqs = [1200, 2400, 3600];
			freqs.forEach((freq, idx) => {
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = 'sine';
				osc.frequency.setValueAtTime(freq, now);

				const initialGain = 0.25 / (idx + 1);
				gain.gain.setValueAtTime(initialGain, now);
				gain.gain.exponentialRampToValueAtTime(0.001, now + 0.7);

				osc.connect(gain);
				gain.connect(destination);
				osc.start(now);
				osc.stop(now + 0.7);
			});
			return 750;
		}
	}
}
